import React, { useEffect, useState, memo, useRef } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { useApi } from '../../../../../../shared/hooks/useApi';
import CustomTable from '../../../../../../shared/components/table/custom-table';
import { LoaderComponent } from '../../../../../../shared/components/loaders/page-loader';
import { fetchaddComponentDetails } from './component-details-apis';

// Row props interface (Component)
export interface Component {
  shipmentId: string;
  productId: string;
  componentId: string;
  tcId: string;
  tcAssociatedProductNo: string;
  tcProductComponentOrder: string;
  tcProductComponentNo: string;
  tcProductComponentDetail: string;
  tcProductComponentNetShippingWeight: number;
  tcProductComponentSupplementaryWeight: number;
}

// RenderDetailPanel props interface
interface RenderDetailPanelProps {
  row: { original: Component };
}

// Column Definitions for Product Component Standard Information
const standardColumns = [
  {
    accessorKey: 'tcProductComponentClaimsCategory',
    header: 'Component Claims Category',
    minSize: 60,
    maxSize: 150,
  },
  {
    accessorKey: 'tcProductComponentCertifiedWeight',
    header: 'Certified Weight (kg)',
    minSize: 60,
    maxSize: 150,
  },
  { accessorKey: 'tcProductComponentClaimsCategoryDescription', 
    header: 'Category Label Description', 
    minSize: 60, 
    maxSize: 150 
  },
];

// Column Definitions for Component Raw Material Information
const rawMaterialColumns = [
  {
    accessorKey: 'tcProductComponentRawMaterialCode',
    header: 'Raw Material Code',
    minSize: 60,
    maxSize: 150,
  },
  {
    accessorKey: 'tcProductComponentRawMaterialPercentage',
    header: 'Raw Material Percentage',
    minSize: 60,
    maxSize: 150,
  },
  { accessorKey: 'tcProductComponentRawMaterialCodeDescription', 
    header: 'Raw Material Description', 
    minSize: 60, 
    maxSize: 150 
  },
];

const RenderDetailPanel = memo(({ row }: RenderDetailPanelProps) => {
  // Extract keys from row data
  const { shipmentId, productId, componentId, tcId } = row.original;

  const { loading, executeGet } = useApi();
  const [componentData, setComponentData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
console.log("component",componentData);

  const hasFetchedRef = useRef(false);

  useEffect(() => {
    console.log("Detail panel row data:", { shipmentId, productId, componentId, tcId });
  
    if (hasFetchedRef.current) return; // only block re-fetching
    hasFetchedRef.current = true;
  
    const fetchData = async () => {
      try {
        const res = await fetchaddComponentDetails(executeGet, tcId, shipmentId, productId, componentId);
        console.log("API response:", res);
        setComponentData(res?.data || res);
      } catch (err: any) {
        setError(err?.message || "Failed to fetch component details");
      }
    };
  
    // Trigger fetch even if some IDs are empty (depending on API)
    fetchData();
  }, [shipmentId, productId, componentId, tcId, executeGet]);
  
  

  if (loading) {
    return <LoaderComponent />;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  if (!componentData) {
    return <Typography>Loading component details...</Typography>;
  }

  // Using the updated accessor keys from your API response
  const standardInfo = componentData?.tcProductComponentInformation[0].tcProductComponentCertifiedWeightInformation || [];
  const rawMaterialInfo = componentData?.tcProductComponentInformation[0].tcProductComponentRawMaterialInformation || [];
console.log(standardInfo,rawMaterialInfo)
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: 5,
        gap: 4,
        width: '100%',
      }}
    >
      {/* Product Component Standard Information */}
      <Box sx={{ width: '100%', textAlign: 'left', mb: 3 }} display="grid">
        <Typography variant="body2" fontWeight="400" sx={{ mb: 1 }}>
          Product Component Standard Information
        </Typography>
        <CustomTable
          columns={standardColumns}
          data={standardInfo}
          enableRowActions={false}
          enableTopToolbar={false}
          enableBottomToolbar={false}
        />
      </Box>
      <Divider />

      {/* Raw Material Composition */}
      <Box sx={{ width: '100%', textAlign: 'left' }} display="grid">
        <Typography variant="body2" fontWeight="400" sx={{ mb: 1 }}>
          Raw Material Composition
        </Typography>
        <CustomTable
          columns={rawMaterialColumns}
          data={rawMaterialInfo}
          enableRowActions={false}
          enableTopToolbar={false}
          enableBottomToolbar={false}
        />
      </Box>
    </Box>
  );
});

export default RenderDetailPanel;
